import React, { useContext } from "react";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import _ from "lodash";
import jssVariables from "../../../../jss_setup";
import {
  DatepickerField,
  SelectField,
} from "../../../forms/inputGroup/helpers";
import { InputField } from "../../../forms/inputGroup";
import {
  StyleTypeContext,
  TranslationContext,
  countries,
} from "../../../../umbilical_tools";
import { DateTime } from "luxon";
import { documentTypes, genderOpts } from "./helpers";
import { useFormikContext } from "formik";

const fieldCustomStyles = {
  label: {
    fontSize: "large",
  },
};

function getSingularAgeType(ageType) {
  if (ageType === "adults") {
    return "adult";
  } else if (ageType === "youths") {
    return "youth";
  } else if (ageType === "seniors") {
    return "senior";
  }
}

const styles = createUseStyles({
  passengerTitle: (props) => ({
    fontSize: "large",
    fontWeight: "bold",
    ..._.get(props, "customStyles.passengerTitle", {}),
  }),
  trvlDocumentContr: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    alignContent: "baseline",
    gridGap: jssVariables.normal_gap,
    background: "#eeecec",
    padding: jssVariables.normal_gap,
    borderRadius: jssVariables.half_gap,
    ..._.get(props, "customStyles.trvlDocumentContr", {}),
  }),
  trvlDocumentLabel: (props) => ({
    fontSize: "large",
    fontWeight: "bold",
    ..._.get(props, "customStyles.trvlDocumentLabel", {}),
  }),
  divider: (props) => ({
    height: "2px",
    background: jssVariables.colors.border,
    width: "100%",
    ..._.get(props, "customStyles.divider", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    trvlDocumentContr: (props) => ({
      gridColumn: "span 3",
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      padding: jssVariables.double_gap,
      ..._.get(props, "customStyles.trvlDocumentContr.smallscreen", {}),
    }),
    trvlDocumentLabel: (props) => ({
      gridColumn: "span 3",
      ..._.get(props, "customStyles.trvlDocumentLabel.smallscreen", {}),
    }),
    passengerTitle: (props) => ({
      gridColumn: "span 3",
      ..._.get(props, "customStyles.passengerTitle.smallscreen", {}),
    }),
    divider: (props) => ({
      gridColumn: "span 3",
      ..._.get(props, "customStyles.divider.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
export const TravellerForm = ({
  ageType,
  requiredFields,
  departure,
  trvlIdxOffset,
}) => {
  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const classes = styles({
    customStyles: cStyler({
      type,
      brand,
      objName: "TravellerForm",
    }),
  });
  const { values } = useFormikContext();
  const { t } = useContext(TranslationContext);

  const defaultReqFields = requiredFields?.defaultReqFields;

  return (
    <React.Fragment>
      {values[ageType].map((traveller, idx) => (
        <React.Fragment key={idx}>
          <div className={classes.passengerTitle}>
            {t("passenger")} {trvlIdxOffset + idx + 1}:{" "}
            {t(getSingularAgeType(ageType))}{" "}
            {traveller.lead_traveler && `(${t("groupLeader")})`}
          </div>
          <SelectField
            id={`title-${idx}`}
            label={t("title")}
            name={`${ageType}.${idx}.title`}
            options={genderOpts}
            customStyles={fieldCustomStyles}
            placeholder="----"
          />
          <InputField
            id={`first_name-${idx}`}
            as="input"
            label={t("firstName")}
            name={`${ageType}.${idx}.first_name`}
            customStyles={fieldCustomStyles}
            placeholder="----"
          />
          <InputField
            id={`last_name-${idx}`}
            as="input"
            label={t("lastName")}
            name={`${ageType}.${idx}.last_name`}
            customStyles={fieldCustomStyles}
            placeholder="----"
          />
          <DatepickerField
            id={`birthday-${idx}`}
            label={t("dateOfBirth")}
            name={`${ageType}.${idx}.date_of_birth`}
            dateFormat="MMM d, yyyy"
            withPortal={true}
            minDate={DateTime.fromISO(departure)
              .minus({ years: traveller.age + 1 })
              .toString()}
            maxDate={DateTime.fromISO(departure)
              .minus({ years: traveller.age })
              .toString()}
            customStyles={fieldCustomStyles}
            placeholder="----"
            showYearDropdown={true}
            yearDropdownItemsNum={90}
          />
          {traveller.lead_traveler && (
            <InputField
              id={`email-${idx}`}
              as="input"
              label={t("email")}
              name={`${ageType}.${idx}.email_address`}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
          )}
          {traveller.lead_traveler && (
            <InputField
              id={`phone-${idx}`}
              as="input"
              label={`${t("countryCode")} + ${t("phone")}`}
              name={`${ageType}.${idx}.phone_number`}
              customStyles={fieldCustomStyles}
              placeholder="+30XXXXXX , 0030XXXXXX"
            />
          )}
          {defaultReqFields?.includes("reduction_card_id") && (
            <InputField
              as="input"
              label={t("reductionCardId")}
              name={`${ageType}.${idx}.reduction_card_id`}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
          )}
          {defaultReqFields?.includes("country_of_residence") && (
            <SelectField
              label={t("countryOfResidence")}
              name={`${ageType}.${idx}.country_of_residence`}
              options={_.sortBy(
                Object.entries(countries).map(([code, name]) => [
                  code,
                  `${name} (${code})`,
                ]),
                (v) => v[1]
              )}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
          )}
          {defaultReqFields?.includes("place_of_birth") && (
            <SelectField
              label={t("countryOfBirth")}
              name={`${ageType}.${idx}.country_of_birth`}
              options={_.sortBy(
                Object.entries(countries).map(([code, name]) => [
                  code,
                  `${name} (${code})`,
                ]),
                (v) => v[1]
              )}
              customStyles={fieldCustomStyles}
              placeholder="----"
            />
          )}
          {defaultReqFields?.includes("passport") && (
            <React.Fragment>
              <div className={classes.trvlDocumentContr}>
                <div className={classes.trvlDocumentLabel}>
                  {t("travelerDocumentInfo")}
                </div>
                <SelectField
                  label={t("documentType")}
                  name={`${ageType}.${idx}.traveler_document.doc_type`}
                  options={documentTypes}
                  customStyles={fieldCustomStyles}
                  placeholder="----"
                />
                <SelectField
                  label={t("documentCountry")}
                  name={`${ageType}.${idx}.traveler_document.country_code`}
                  options={_.sortBy(
                    Object.entries(countries).map(([code, name]) => [
                      code,
                      `${name} (${code})`,
                    ]),
                    (v) => v[1]
                  )}
                  customStyles={fieldCustomStyles}
                  placeholder="----"
                />
                <InputField
                  as="input"
                  label={t("documentNumber")}
                  name={`${ageType}.${idx}.traveler_document.document_number`}
                  customStyles={fieldCustomStyles}
                  placeholder="----"
                />
                <DatepickerField
                  label={t("expirationDate")}
                  name={`${ageType}.${idx}.traveler_document.expiration_date`}
                  dateFormat="MMM d, yyyy"
                  withPortal={true}
                  minDate={DateTime.fromISO(departure)
                    .plus({ month: 6 })
                    .toISO()}
                  customStyles={fieldCustomStyles}
                  placeholder="----"
                  showYearDropdown={true}
                  yearDropdownItemsNum={90}
                />
              </div>
            </React.Fragment>
          )}
          {idx + 1 < values[ageType].length && (
            <hr className={classes.divider} />
          )}
        </React.Fragment>
      ))}
    </React.Fragment>
  );
};
TravellerForm.defaultProps = {
  ageType: "adults",
  requiredFields: {},
  departure: "",
  trvlIdxOffset: 0,
};
TravellerForm.propTypes = {
  ageType: PropTypes.string.isRequired,
  requiredFields: PropTypes.object.isRequired,
  departure: PropTypes.string.isRequired,
  trvlIdxOffset: PropTypes.number.isRequired,
};
